/// <amd-module name="Artifacts/PackageRecycleBin/hooks/RecycleBinCommands" />

import * as Res from "Artifacts/PackageRecycleBin/Resources";
import { PermanentDeleteModalId } from "Artifacts/Root/components/PermanentDeleteModal";
import { RestoreModalId } from "Artifacts/Root/components/RestoreModal";
import { injectModal } from "Artifacts/Root/protocols/Commands";
import {
  filterSelectedPackages,
  getAggregatedCommands,
  groupPackagesByProtocol,
} from "Artifacts/Root/protocols/CrossProtocolTools";
import { ICommand } from "Artifacts/Root/protocols/types";
import { getStore } from "Artifacts/Root/Redux";
import { IFeed, IPackageVersionIdentity } from "Artifacts/Root/types";
import { useCallback, useEffect, useState } from "react";
import { announce } from "VSS/Core/Util/Accessibility";
import { useComponentContext } from "VSS/Platform/Layout";
import { IListSelection } from "VSSUI/List";
import { PackageRecycleBinActions } from "../redux/Actions";

/**
 * useCrossProtocolCommands is a hook that enables cross protocol commands based on package version table selection.
 * @param pkgVersions array of package versions from the table
 * @param selection object representing selected versions from the table.
 * @returns list of available commands.
 */
export function useCrossProtocolCommands(
  pkgVersions: IPackageVersionIdentity[] | undefined,
  selection: IListSelection,
  feed?: IFeed,
  tagFilters?: Set<string>,
): ICommand[] {
  const { pageContext } = useComponentContext();
  const [commands, setCommands] = useState<ICommand[]>([]);
  const store = getStore(pageContext);
  const dispatch = store.dispatch;

  // TODO: validate if this work with table sorting behavior.
  // onSelect retrieves available commands, aggregates
  // and combines commands with the same id into aggregate command.
  const onSelect = useCallback((): void => {
    if (pkgVersions) {
      const protocolToVersionsMap = groupPackagesByProtocol(selection, pkgVersions);
      const selectedVersions = filterSelectedPackages(selection, pkgVersions);
      const permanentDeleteModalInjector = injectModal(
        pageContext,
        "permanent-delete",
        PermanentDeleteModalId,
        (command, hideModal) => ({
          versions: selectedVersions,
          onDismiss: () => {
            hideModal();
          },
          onPermanentDelete: () => {
            command.action({
              feed,
              selectedVersions,
              onSuccessCallback: (selectedVersions: IPackageVersionIdentity[]) => {
                dispatch(PackageRecycleBinActions.fakePackagePermanentDelete(selectedVersions));
                announce(Res.PermanentlyDeleted_Announce);
                selection.clear();
                hideModal();
              },
            });
          },
        }),
      );

      const restoreModalInjector = injectModal(
        pageContext,
        "restore",
        RestoreModalId,
        (command, hideModal) => ({
          versions: selectedVersions,
          onDismiss: () => {
            hideModal();
          },
          onRestore: () => {
            command.action({
              feed,
              selectedVersions,
              onSuccessCallback: (selectedVersions: IPackageVersionIdentity[]) => {
                dispatch(PackageRecycleBinActions.fakePackagePermanentDelete(selectedVersions));
                announce(Res.Restored_Announce);
                selection.clear();
                hideModal();
              },
            });
          },
        }),
      );

      const aggregatedCommands = restoreModalInjector(
        permanentDeleteModalInjector(
          getAggregatedCommands(pageContext, protocolToVersionsMap, feed, tagFilters),
        ),
      );
      setCommands(aggregatedCommands);
    }
  }, [pkgVersions, selection, pageContext, tagFilters, feed, dispatch]);

  useEffect(() => {
    selection.subscribe(onSelect);
    return () => {
      selection.unsubscribe(onSelect);
    };
  }, [onSelect, selection, setCommands]);

  return commands;
}
